/**
 * Broker Intelligence API Service
 * Centralized service for all broker dashboard API calls
 */

interface BrokerBillingResponse {
  success: boolean;
  billing: {
    current_month: {
      base_fee: number;
      upgrade_fees: number;
      total_amount: number;
      agent_counts: {
        total: number;
        essential: number;
        professional: number;
        premium: number;
      }
    };
    payment_method: string | null;
    plan_type: string;
    max_agents: number;
    billing_history: any[];
  };
}

interface AgentProfile {
  id: string;
  agent_full_name: string;
  agent_email: string;
  agent_phone: string;
  agent_license_number: string;
  license_type: 'BR' | 'SA';
  individual_plan: 'essential' | 'professional' | 'premium';
  monthly_fee: number;
  is_active: number;
  last_activity?: string;
}

interface CompanyProfileData {
  brokerage_id: string;
  company_name: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  license_number?: string;
  mls_id?: string;
  office_short_id?: string;
  mls_access?: string | boolean;
  association_id?: string | number;
  mls_login_id?: string;
  agreed_to_terms?: boolean;
}

interface CompanyProfileResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: CompanyProfileData | {
    brokerage_id: string;
    brokerage_name: string;
    company_data: Partial<CompanyProfileData>;
    mls_data?: Partial<CompanyProfileData>;
  };
}

interface CompanyProfilePayload {
  company_name: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  license_number?: string;
  mls_id?: string;
  office_short_id?: string;
  mls_access?: string | boolean;
  association_id?: string | number | null;
  mls_login_id?: string;
  agreed_to_terms?: boolean;
}

class BrokerApiService {
  // Dynamic base URL detection for environment compatibility
  private static getApiBase(endpoint: string) {
    // Check if we're in production (not localhost)
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    
    if (isProduction) {
      return endpoint;  // Use relative path in production
    }
    
    // In development, use full URL
    return `http://localhost:8080${endpoint}`;
  }
  
  // Use dynamic detection for base URLs
  private static get baseUrl() {
    return this.getApiBase('/api/broker');
  }
  
  // Commission endpoints use the main API path, not broker-specific
  private static get commissionBaseUrl() {
    return this.getApiBase('/api');
  }
    
  // User endpoints (for Stripe invoices etc)
  private static get userBaseUrl() {
    return this.getApiBase('/api/user');
  }
  
  private static getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get current billing information for a brokerage
   */
  static async getBillingInfo(brokerageId: string): Promise<BrokerBillingResponse> {
    try {
      // Add timeout to prevent Gateway Timeout issues
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${this.baseUrl}/billing/${brokerageId}`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch billing info: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - billing service is temporarily unavailable');
      }
      throw new Error(`Failed to fetch billing info: ${error.message}`);
    }
  }

  /**
   * Get the company profile tied to the authenticated user
   */
  static async getCompanyProfile(): Promise<CompanyProfileResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const emptyStateMessage = 'No brokerage details saved yet. Complete the form below to get started.';
    const transientIssueMessage = 'We could not load your brokerage details right now. Please complete the form below or try again shortly.';

    try {
      const url = `${this.baseUrl}/company-profile`;
      console.log('🔗 [BrokerApiService] Fetching company profile from:', url);
      console.log('🔐 [BrokerApiService] Auth headers:', this.getAuthHeaders());
      
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
        signal: controller.signal
      });

      if (!response.ok) {
        let responseBody = '';
        try {
          responseBody = await response.text();
        } catch (readError) {
          console.warn('⚠️ [BrokerApiService] Unable to read company profile error body:', readError);
        }

        console.warn('⚠️ [BrokerApiService] Company profile request failed:', response.status, responseBody);

        const lowerBody = responseBody.toLowerCase();
        const noProfile = response.status === 404 || lowerBody.includes('no company profile');

        return {
          success: false,
          message: noProfile ? emptyStateMessage : transientIssueMessage
        };
      }

      return await response.json();
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.warn('⚠️ [BrokerApiService] Company profile request timed out');
        return {
          success: false,
          message: transientIssueMessage
        };
      }

      console.warn('⚠️ [BrokerApiService] Company profile request errored:', error);
      return {
        success: false,
        message: transientIssueMessage
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get billing history for a brokerage
   */
  static async getBillingHistory(brokerageId: string, limit = 12, offset = 0) {
    const response = await fetch(
      `${this.baseUrl}/billing/${brokerageId}/history?limit=${limit}&offset=${offset}`,
      {
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch billing history: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get agent list for a brokerage
   */
  static async getAgents(brokerageId: string): Promise<AgentProfile[]> {
    try {
      // Add timeout to prevent Gateway Timeout issues
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(`${this.baseUrl}/agents/${brokerageId}`, {
        headers: {
          ...this.getAuthHeaders(),
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.agents : [];
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - agents service is temporarily unavailable');
      }
      throw new Error(`Failed to fetch agents: ${error.message}`);
    }
  }

  /**
   * Update an agent's plan
   */
  static async updateAgentPlan(
    agentId: string, 
    newPlan: 'essential' | 'professional' | 'premium',
    effectiveDate?: string,
    upgradeReason?: string
  ) {
    const response = await fetch(`${this.baseUrl}/agents/${agentId}/plan`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        new_plan: newPlan,
        effective_date: effectiveDate,
        upgrade_reason: upgradeReason
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to update agent plan: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Bulk update multiple agents' plans
   */
  static async bulkUpdateAgentPlans(
    agentIds: string[],
    newPlan: 'essential' | 'professional' | 'premium',
    effectiveDate?: string,
    upgradeReason?: string
  ) {
    const response = await fetch(`${this.baseUrl}/agents/bulk-plan-update`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        agent_ids: agentIds,
        new_plan: newPlan,
        effective_date: effectiveDate,
        upgrade_reason: upgradeReason
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to bulk update agent plans: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Persist company profile changes for the authenticated brokerage owner
   */
  static async saveCompanyProfile(payload: CompanyProfilePayload): Promise<CompanyProfileResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`${this.baseUrl}/company-profile`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save company profile: ${response.status} ${errorText}`);
      }

      return response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Company profile save timed out');
      }
      throw error;
    }
  }

  /**
   * Create payment session for billing
   */
  static async createPaymentSession(brokerageId: string, billingRecordId: string, returnUrl?: string) {
    const response = await fetch(`${this.baseUrl}/billing/${brokerageId}/create-payment-session`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        billing_record_id: billingRecordId,
        return_url: returnUrl
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create payment session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Calculate current billing
   */
  static async calculateBilling(brokerageId: string) {
    const response = await fetch(`${this.baseUrl}/billing/${brokerageId}/calculate`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to calculate billing: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get agent upgrade history
   */
  static async getAgentUpgradeHistory(agentId: string) {
    const response = await fetch(`${this.baseUrl}/agents/${agentId}/upgrade-history`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch upgrade history: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * MLS ONBOARDING METHODS
   */

  /**
   * Validate office exists in MLS
   */
  static async validateOffice(officeShortId: string) {
    const response = await fetch(`${this.baseUrl}/validate-office`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        officeShortId: officeShortId
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to validate office: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Preview onboarding data (agents, pricing, etc.)
   */
  static async previewOnboarding(officeShortId: string, planType: 'team' | 'corporate' | 'enterprise') {
    const response = await fetch(`${this.baseUrl}/preview-onboarding`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        officeShortId: officeShortId,
        planType: planType
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to preview onboarding: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Fast onboard brokerage with all agents
   */
  static async fastOnboard(officeShortId: string, planType: 'team' | 'corporate' | 'enterprise') {
    const response = await fetch(`${this.baseUrl}/fast-onboard`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        officeShortId: officeShortId,
        planType: planType
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to onboard brokerage: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get user's brokerages
   */
  static async getMyBrokerages() {
    const response = await fetch(`${this.baseUrl}/my-brokerages`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch brokerages: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get current user's brokerage information
   * This replaces hardcoded brokerage ID lookups
   */
  static async getCurrentUserBrokerage(): Promise<{ brokerageId: string; brokerageName: string } | null> {
    try {
      const response = await fetch(`${this.baseUrl}/my-brokerages`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user brokerage: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🔍 [BrokerApiService] Raw brokerages response:', data);
      
      if (data.success && data.brokerages && data.brokerages.length > 0) {
        // Look for KairosIQ AI Realty first, then fall back to marked primary, then first
        let primaryBrokerage = data.brokerages.find(b => 
          b.brokerage_name && b.brokerage_name.toLowerCase().includes('kairosiq')
        );
        
        if (!primaryBrokerage) {
          // Look for one marked as primary
          primaryBrokerage = data.brokerages.find(b => b.is_primary === true);
        }
        
        if (!primaryBrokerage) {
          // Fall back to first brokerage
          primaryBrokerage = data.brokerages[0];
        }
        
        console.log('🔍 [BrokerApiService] Selected primary brokerage:', primaryBrokerage);
        
        return {
          brokerageId: primaryBrokerage.id.toString(),
          brokerageName: primaryBrokerage.brokerage_name
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user brokerage:', error);
      return null;
    }
  }

  /**
   * Get brokerage dashboard data
   */
  static async getBrokerageDashboard(brokerageId: string) {
    const response = await fetch(`${this.baseUrl}/dashboard/${brokerageId}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch brokerage dashboard: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get Transaction AI metrics for enhanced dashboard
   */
  static async getTransactionAIMetrics(brokerageId: string) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${this.baseUrl}/transaction-ai-metrics/${brokerageId}`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch Transaction AI metrics: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - Transaction AI metrics service is temporarily unavailable');
      }
      throw new Error(`Failed to fetch Transaction AI metrics: ${error.message}`);
    }
  }

  /**
   * Get communication metrics for office collaboration
   */
  static async getCommunicationMetrics(brokerageId: string) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${this.baseUrl}/communication-metrics/${brokerageId}`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch communication metrics: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - communication metrics service is temporarily unavailable');
      }
      throw new Error(`Failed to fetch communication metrics: ${error.message}`);
    }
  }

  /**
   * Get activity stream for intelligence feed
   */
  static async getActivityStream(brokerageId: string, limit = 20) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${this.baseUrl}/activity-stream/${brokerageId}?limit=${limit}`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch activity stream: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - activity stream service is temporarily unavailable');
      }
      throw new Error(`Failed to fetch activity stream: ${error.message}`);
    }
  }

  /**
   * COMMISSION TRACKING METHODS
   */

  /**
   * Update agent commission settings
   */
  static async updateAgentCommissionSettings(agentId: string, data: any) {
    try {
      const response = await fetch(`${this.commissionBaseUrl}/commission/agent/${agentId}/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to update commission settings: ${response.statusText}`);
      }

      return { success: true, data: await response.json() };
    } catch (error: any) {
      console.error('Error updating agent commission settings:', error);
      return {
        success: false,
        message: error.message || 'Failed to update commission settings'
      };
    }
  }

  /**
   * Get agent commission data
   */
  static async getAgentCommissionData(agentId: string, year?: number) {
    try {
      const params = year ? `?year=${year}` : '';
      const response = await fetch(`${this.commissionBaseUrl}/commission/agent/${agentId}${params}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch commission data: ${response.statusText}`);
      }

      return { success: true, data: await response.json() };
    } catch (error: any) {
      console.error('Error fetching agent commission data:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch commission data'
      };
    }
  }

  /**
   * Get brokerage agents with commission data
   */
  static async getBrokerageAgentsWithCommission(brokerageId: string) {
    try {
      const response = await fetch(`${this.commissionBaseUrl}/commission/brokerage/${brokerageId}/agents`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.statusText}`);
      }

      return { success: true, data: await response.json() };
    } catch (error: any) {
      console.error('Error fetching brokerage agents:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch agents'
      };
    }
  }

  /**
   * Generate CDA for a deal
   */
  static async generateCDA(dealId: string, cdaData: any) {
    try {
      const response = await fetch(`${this.commissionBaseUrl}/commission/cda/generate/${dealId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(cdaData)
      });

      if (!response.ok) {
        throw new Error(`Failed to generate CDA: ${response.statusText}`);
      }

      return { success: true, data: await response.json() };
    } catch (error: any) {
      console.error('Error generating CDA:', error);
      return {
        success: false,
        message: error.message || 'Failed to generate CDA'
      };
    }
  }

  /**
   * Get CDA by ID
   */
  static async getCDA(cdaId: string) {
    try {
      const response = await fetch(`${this.commissionBaseUrl}/commission/cda/${cdaId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CDA: ${response.statusText}`);
      }

      return { success: true, data: await response.json() };
    } catch (error: any) {
      console.error('Error fetching CDA:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch CDA'
      };
    }
  }

  /**
   * Get all transactions for agents in the brokerage
   */
  static async getTransactions(brokerageId: string, filters?: {
    stage?: string;
    compliance_status?: string;
    agent_id?: string;
    limit?: number;
    offset?: number;
  }) {
    try {
      const params = new URLSearchParams();
      if (filters?.stage) params.append('stage', filters.stage);
      if (filters?.compliance_status) params.append('compliance_status', filters.compliance_status);
      if (filters?.agent_id) params.append('agent_id', filters.agent_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(
        `${this.baseUrl}/transactions${params.toString() ? '?' + params.toString() : ''}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching broker transactions:', error);
      throw error;
    }
  }

  /**
   * Get transaction details
   */
  static async getTransactionDetails(brokerageId: string, transactionId: string) {
    try {
      const response = await fetch(
        `${this.baseUrl}/transactions/${transactionId}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch transaction details: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching transaction details:', error);
      throw error;
    }
  }

  /**
   * Update transaction stage
   */
  static async updateTransactionStage(transactionId: string, stage: string) {
    try {
      const response = await fetch(
        `/api/transactions/${transactionId}/stage`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders()
          },
          body: JSON.stringify({ stage })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update transaction stage: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error updating transaction stage:', error);
      throw error;
    }
  }
}

export default BrokerApiService;
export type { BrokerBillingResponse, AgentProfile };
